import React, { useState } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

export default function CheckBalance() {
  const { getMessage, translate } = useTranslation();
  const [searchType, setSearchType] = useState('license_plate');
  const [searchValue, setSearchValue] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [showVehicleSelection, setShowVehicleSelection] = useState(false);

  const searchTypes = [
    { value: 'license_plate', label: `🚗 ${getMessage('check_balance.search_types.license_plate')}`, placeholder: getMessage('check_balance.search_placeholders.license_plate') },
    { value: 'obu_device_id', label: `📡 ${getMessage('check_balance.search_types.obu_device_id')}`, placeholder: getMessage('check_balance.search_placeholders.obu_device_id') },
    { value: 'card_uid', label: `💳 ${getMessage('check_balance.search_types.card_uid')}`, placeholder: getMessage('check_balance.search_placeholders.card_uid') }
  ];

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchValue.trim()) {
      toast.error(`❌ ${getMessage('error_messages.search_required')}`);
      return;
    }

    setLoading(true);

    if (searchType === 'license_plate') {
      try {
        const searchResponse = await API.get(`/wallet/search-vehicles-by-plate`, {
          params: { license_plate: searchValue.trim() }
        });

        if (searchResponse.data && searchResponse.data.count >= 2) {
          setVehicleOptions(searchResponse.data.vehicles);
          setShowVehicleSelection(true);
          setBalanceData(null);
          setLoading(false);
          toast(`🔍 ${translate('check_balance.found_multiple', { count: searchResponse.data.count, plate: searchValue.trim() })}`, {
            icon: '⚠️',
            duration: 4000
          });
          return;
        }
      } catch (error) {
        console.error('Multiple vehicle check error:', error);
      }
    }

    try {
      const response = await API.get(`/wallet/check-balance`, {
        params: {
          type: searchType,
          value: searchValue.trim()
        }
      });

      setBalanceData(response.data);
      setShowVehicleSelection(false);

      const newSearch = {
        type: searchType,
        value: searchValue.trim(),
        timestamp: new Date(),
        label: searchTypes.find(t => t.value === searchType)?.label
      };

      setRecentSearches(prev => {
        const filtered = prev.filter(search =>
          !(search.type === searchType && search.value === searchValue.trim())
        );
        return [newSearch, ...filtered].slice(0, 10);
      });

      toast.success(`✅ ${getMessage('check_balance.balance_found')}`);
    } catch (error) {
      console.error('Check balance error:', error);
      setBalanceData(null);
      if (error.response?.status === 404) {
        toast.error(`❌ ${getMessage('check_balance.no_data_found')}`);
      } else {
        toast.error(`❌ ${getMessage('error_messages.check_balance_failed')}: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = async (vehicle) => {
    setLoading(true);
    try {
      let searchTypeForDevice;
      let searchValueForDevice;

      if (vehicle.device_type === 'obu' || vehicle.device_type === 'obu_sbu') {
        searchTypeForDevice = 'obu_device_id';
        searchValueForDevice = vehicle.obu_device_id;
      } else if (vehicle.device_type === 'mifare') {
        searchTypeForDevice = 'card_uid';
        searchValueForDevice = vehicle.card_uid;
      } else {
        if (vehicle.card_uid && vehicle.card_uid !== 'N/A') {
          searchTypeForDevice = 'card_uid';
          searchValueForDevice = vehicle.card_uid;
        } else if (vehicle.obu_device_id && vehicle.obu_device_id !== 'N/A') {
          searchTypeForDevice = 'obu_device_id';
          searchValueForDevice = vehicle.obu_device_id;
        }
      }

      if (!searchValueForDevice || searchValueForDevice === 'N/A' || searchValueForDevice === null) {
        toast.error(`❌ Cannot find valid device identifier for this vehicle.`);
        setLoading(false);
        return;
      }

      const response = await API.get(`/wallet/check-balance`, {
        params: {
          type: searchTypeForDevice,
          value: searchValueForDevice
        }
      });

      setBalanceData(response.data);
      setShowVehicleSelection(false);
      toast.success(`✅ ${getMessage('check_balance.balance_found')}`);
    } catch (error) {
      console.error('Check balance error:', error);
      toast.error(`❌ ${getMessage('error_messages.check_balance_failed')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecentSearch = (search) => {
    setSearchType(search.type);
    setSearchValue(search.value);
  };

  const clearResults = () => {
    setBalanceData(null);
    setSearchValue('');
    setShowVehicleSelection(false);
    setVehicleOptions([]);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
            background: white !important;
          }
          #thermal-receipt, #thermal-receipt * {
            visibility: visible;
          }
          #thermal-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            padding: 5mm;
            margin: 0;
            display: block !important;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: auto;
            margin: 0;
          }
        }
      `}</style>

      {/* Main Content (Screen Only Visibility Helper) */}
      <div className="no-print">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            💰 {getMessage('check_balance.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {getMessage('check_balance.description')}
          </p>
        </div>

        {/* Search Form */}
        <div className="mb-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {getMessage('check_balance.search_type')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {searchTypes.map(type => (
                  <label key={type.value} className="flex items-center">
                    <input
                      type="radio"
                      name="searchType"
                      value={type.value}
                      checked={searchType === type.value}
                      onChange={(e) => {
                        setSearchType(e.target.value);
                        setSearchValue('');
                        setBalanceData(null);
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm font-bold">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  required
                  placeholder={searchTypes.find(st => st.value === searchType)?.placeholder}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white text-lg"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-3 rounded-lg text-white font-bold transition-colors duration-200 ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {getMessage('common.search')}...
                  </div>
                ) : (
                  `🔍 ${getMessage('check_balance.check_button')}`
                )}
              </button>

              {balanceData && (
                <button
                  type="button"
                  onClick={clearResults}
                  className="px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                >
                  ❌ {getMessage('check_balance.clear_button')}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Results for balanceData */}
        {balanceData && !showVehicleSelection && (
          <div className="space-y-6">
            {/* Main Balance Card */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium opacity-90 mb-1">{getMessage('check_balance.balance_amount')}</h2>
                  <div className="text-5xl font-black tracking-tight">
                    {parseFloat(balanceData.balance || 0).toLocaleString()} <span className="text-2xl opacity-80">LAK</span>
                  </div>
                </div>
                <div className="text-7xl opacity-30">💰</div>
              </div>
            </div>

            {/* Account Info UI (Screen) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-700/50 rounded-xl border-2 border-gray-100 dark:border-gray-600 p-5">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 border-b pb-2">
                  👤 {getMessage('check_balance.account_info')}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('users.phone')}:</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">{balanceData.phone || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('check_balance.search_types.card_uid')}:</span>
                    <span className="font-mono font-bold text-gray-800 dark:text-white">{balanceData.card_uid || balanceData.obu_device_id || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('vehicles.license_plate')}:</span>
                    <span className="font-black text-xl text-gray-900 dark:text-white underline">{balanceData.license_plate || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-700/50 rounded-xl border-2 border-gray-100 dark:border-gray-600 p-5">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 border-b pb-2">
                  📊 {getMessage('check_balance.usage_stats')}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('check_balance.total_transactions')}:</span>
                    <span className="font-bold text-gray-800 dark:text-white">{balanceData.total_transactions || 0} {getMessage('check_balance.times_suffix')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('check_balance.total_topups')}:</span>
                    <span className="font-bold text-green-600">+{parseFloat(balanceData.total_topups || 0).toLocaleString()} LAK</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400 font-bold">{getMessage('check_balance.total_spending')}:</span>
                    <span className="font-bold text-red-600">-{parseFloat(balanceData.total_spending || 0).toLocaleString()} LAK</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions (Screen) */}
            {balanceData.recent_transactions && balanceData.recent_transactions.length > 0 && (
              <div className="bg-white dark:bg-gray-700/50 rounded-xl border-2 border-gray-100 dark:border-gray-600 p-5">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                  📜 {translate('check_balance.recent_transactions_list', { count: balanceData.recent_transactions.length })}
                </h3>
                <div className="space-y-2">
                  {balanceData.recent_transactions.map((tx, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-lg border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <span className={tx.type === 'topup' ? 'text-green-500' : 'text-red-500'}>
                          {tx.type === 'topup' ? '⬆️' : '⬇️'}
                        </span>
                        <div>
                          <div className="font-bold text-gray-800 dark:text-white">{tx.description || (tx.type === 'topup' ? getMessage('check_balance.transaction_topup') : getMessage('check_balance.transaction_toll'))}</div>
                          <div className="text-xs text-gray-400 font-medium">{new Date(tx.created_at).toLocaleString('th-TH')}</div>
                        </div>
                      </div>
                      <div className={`font-black ${tx.type === 'topup' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'topup' ? '+' : '-'}{parseFloat(tx.amount).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => window.print()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2"
              >
                🖨️ {getMessage('check_balance.print_report')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 
        🖨️ PHYSICAL RECEIPT FOR THERMAL PRINTER
        This div is invisible on screen but becomes visible and positioned absolutely on print via the <style> block above.
      */}
      {balanceData && (
        <div id="thermal-receipt" className="hidden text-black font-sans leading-tight">
          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 5px 0' }}>SMART TOLL SYSTEM</h1>
            <div style={{ borderTop: '2px dashed #000', margin: '5px 0' }}></div>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', textDecoration: 'underline' }}>ໃບແຈ້ງຍອດເງິນຍັງເຫຼືອ</h2>
            <p style={{ fontSize: '10px', fontStyle: 'italic', margin: '2px 0' }}>Balance Inquiry Receipt</p>
          </div>

          <div style={{ fontSize: '11px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>ວັນທີ: {new Date().toLocaleDateString('th-TH')}</span>
              <span>ເວລາ: {new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>

          <div style={{ borderTop: '1px dashed #444', margin: '5px 0' }}></div>

          <div style={{ fontSize: '12px', spaceY: '3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>ID / UID:</span>
              <span style={{ fontFamily: 'monospace' }}>{balanceData.card_uid || balanceData.obu_device_id || '-'}</span>
            </div>
            {balanceData.license_plate && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ fontWeight: 'bold' }}>ທະບຽນ:</span>
                <span style={{ fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #000' }}>{balanceData.license_plate}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>ປະເພດ:</span>
              <span style={{ fontWeight: 'bold' }}>{balanceData.obu_device_id ? '📡 OBU-RBU' : '💳 UHF CARD'}</span>
            </div>
          </div>

          <div style={{ borderTop: '2px solid #000', margin: '10px 0' }}></div>

          <div style={{ textAlign: 'center', padding: '15px 0', border: '1px solid #000', borderRadius: '5px', margin: '15px 0' }}>
            <p style={{ fontSize: '10px', fontWeight: 'bold', margin: '0 0 5px 0' }}>ຍອດເງິນຍັງເຫຼືອ (BALANCE)</p>
            <h2 style={{ fontSize: '26px', fontWeight: 'bold', margin: '0' }}>{parseFloat(balanceData.balance || 0).toLocaleString()} <span style={{ fontSize: '14px' }}>LAK</span></h2>
          </div>

          {balanceData.recent_transactions && balanceData.recent_transactions.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '11px', fontWeight: 'bold', borderBottom: '1px dashed #000', paddingBottom: '2px', marginBottom: '5px' }}>ລາຍການຫຼ້າສຸດ:</p>
              {balanceData.recent_transactions.slice(0, 3).map((tx, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '1px 0' }}>
                  <span style={{ fontStyle: 'italic' }}>{new Date(tx.created_at).toLocaleDateString()} {tx.type === 'topup' ? 'ເຕີມເງິນ' : 'ຜ່ານດ່ານ'}</span>
                  <span style={{ fontWeight: 'bold' }}>{tx.type === 'topup' ? '+' : '-'}{parseFloat(tx.amount).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ borderTop: '2px dashed #000', paddingTop: '10px', textAlign: 'center', marginTop: '20px' }}>
            <p style={{ fontSize: '14px', fontWeight: 'bold', margin: '0' }}>ຂໍຂອບໃຈທີ່ໃຊ້ບໍລິການ</p>
            <p style={{ fontSize: '10px', fontStyle: 'italic', margin: '2px 0' }}>Thank you for using our service</p>
            <div style={{ marginTop: '10px', fontSize: '8px', color: '#666' }}>
              <p>Generated by: Smart Gate System - {process.env.GATE || 'BOK-01'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Selection for Duplicates */}
      {showVehicleSelection && vehicleOptions.length > 0 && (
        <div className="no-print mt-6 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl border-2 border-yellow-200">
          <h3 className="font-bold text-yellow-800 dark:text-yellow-200 mb-4">⚠️ Found {vehicleOptions.length} vehicles with this plate. Please select:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {vehicleOptions.map(v => (
              <div key={v.id} onClick={() => handleVehicleSelect(v)} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow cursor-pointer hover:border-blue-500 border-2 border-transparent transition-all">
                <div className="font-black text-xl text-blue-600">{v.license_plate}</div>
                <div className="text-sm text-gray-500">{v.owner_name} - {v.vehicle_type}</div>
                <div className="mt-2 text-xs font-mono">{v.card_uid || v.obu_device_id}</div>
              </div>
            ))}
          </div>
          <button onClick={clearResults} className="w-full mt-4 py-2 bg-gray-500 text-white rounded-lg">Cancel</button>
        </div>
      )}
    </div>
  );
}